import { CheckCircle, XCircle } from "../../../components/icons/svg/outline";
import { isDayWithinJobActiveRange } from "../../planning/schedule/model/scheduleJobDateRange";
import {
  getJobActiveStatusLabel,
  getProjectTitle,
} from "../../../utils/business";
import { getTodayInPT, toProjectDateKey } from "../../../utils/datetime";
import { getUserDisplayName } from "../../../utils/business/user";
import { PROJECTS_HOME_SORT_MODES } from "../constants";

/**
 * Whether the project/job is date-window active today.
 *
 * Prefers API ``active`` from ``JobNumberSerializer`` /
 * ``is_job_date_window_active``. Falls back to the shared client date-window
 * helper (same rules) when the payload has no ``active`` field.
 *
 * @param {Object|null|undefined} project - Project / JobNumber row.
 * @returns {boolean}
 */
export function isProjectActiveToday(project) {
  if (!project) {
    return false;
  }
  if (typeof project.active === "boolean") {
    return project.active;
  }
  return isDayWithinJobActiveRange(getTodayInPT(), project);
}

/**
 * Return status chip config from date-window active status (Active / Inactive).
 *
 * @param {boolean|Object} isActiveOrProject - Active flag, or project row to evaluate.
 * @param {"home"|"header"} [variant="home"] - Surface-specific chip colors.
 * @returns {{ label: string, Icon: React.ComponentType<{className?: string}>, className: string, isActive: boolean }}
 */
export function getProjectStatusConfig(isActiveOrProject, variant = "home") {
  const isActive =
    typeof isActiveOrProject === "boolean"
      ? isActiveOrProject
      : isProjectActiveToday(isActiveOrProject);
  if (isActive) {
    return {
      label: getJobActiveStatusLabel(true),
      Icon: CheckCircle,
      className:
        variant === "header"
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-primary bg-primary text-white",
      isActive: true,
    };
  }
  return {
    label: getJobActiveStatusLabel(false),
    Icon: XCircle,
    className: "border-gray-200 bg-gray-100 text-gray-600",
    isActive: false,
  };
}

/** Shared chip chrome for status + manager pills on Projects Home surfaces. */
export const PROJECT_HOME_CHIP_CLASS =
  "inline-flex h-4 items-center gap-0.5 rounded-full border px-1.5 text-[10px] max-h-[16px] font-normal leading-none";

/** Manager pills — muted gray for the grayscale card chrome. */
export const PROJECT_HOME_MANAGER_CHIP_CLASS =
  "border-gray-200 bg-gray-100 text-gray-700";

/**
 * Format a brief card metric for display (whole number when possible).
 * Zero amounts render as a plain hyphen.
 *
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatProjectCardMetric(value) {
  const n = Number(value) || 0;
  if (n === 0) {
    return "-";
  }
  if (Number.isInteger(n)) {
    return String(n);
  }
  return n.toFixed(1);
}

/**
 * Empty concrete/labor brief summary for a project card.
 *
 * @returns {{ concreteToday: number, concrete7d: number, laborToday: number, labor7d: number }}
 */
export function emptyProjectCardOpsSummary() {
  return {
    concreteToday: 0,
    concrete7d: 0,
    laborToday: 0,
    labor7d: 0,
  };
}

/**
 * Normalize a Projects Home ops-summary API row for card display.
 *
 * @param {Object|null|undefined} row - Row from ``GET /api/projects/ops-summaries/``.
 * @returns {{ concreteToday: number, concrete7d: number, laborToday: number, labor7d: number }}
 */
export function normalizeProjectCardOpsSummary(row) {
  return {
    concreteToday: Number(row?.concreteToday) || 0,
    concrete7d: Number(row?.concrete7d) || 0,
    laborToday: Number(row?.laborToday) || 0,
    labor7d: Number(row?.labor7d) || 0,
  };
}

/**
 * Whether a project matches the Projects Home start/end date filter.
 *
 * - Both empty: inactive (always matches).
 * - Both set: project must have both dates and be fully contained (inclusive).
 * - Start only: ``date_start >= filterStart``; ignore ``date_end``.
 * - End only: ``date_end <= filterEnd``; ignore ``date_start``.
 * - Both set with start > end: no matches.
 *
 * @param {Object} project - Project API row.
 * @param {string} filterStart - Filter start (`YYYY-MM-DD` or empty).
 * @param {string} filterEnd - Filter end (`YYYY-MM-DD` or empty).
 * @returns {boolean}
 */
export function projectMatchesDateRangeFilter(project, filterStart, filterEnd) {
  const hasStart = Boolean(filterStart);
  const hasEnd = Boolean(filterEnd);
  if (!hasStart && !hasEnd) {
    return true;
  }
  if (hasStart && hasEnd && filterStart > filterEnd) {
    return false;
  }

  const projectStart = toProjectDateKey(project?.date_start);
  const projectEnd = toProjectDateKey(project?.date_end);

  if (hasStart && hasEnd) {
    if (!projectStart || !projectEnd) {
      return false;
    }
    return projectStart >= filterStart && projectEnd <= filterEnd;
  }

  if (hasStart) {
    return Boolean(projectStart) && projectStart >= filterStart;
  }

  return Boolean(projectEnd) && projectEnd <= filterEnd;
}

/**
 * Build sorted customer multi-select options from loaded projects.
 *
 * @param {Array<Object>} projects - Project API rows.
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildProjectCustomerFilterOptions(projects) {
  const byId = new Map();
  for (const project of projects || []) {
    const id = project?.customer?.id;
    const name = project?.customer?.name;
    if (id == null || !name) {
      continue;
    }
    byId.set(String(id), name);
  }
  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Build sorted manager multi-select options from loaded projects.
 *
 * @param {Array<Object>} projects - Project API rows.
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildProjectManagerFilterOptions(projects) {
  const byId = new Map();
  for (const project of projects || []) {
    for (const manager of project?.managers || []) {
      if (manager?.id == null) {
        continue;
      }
      const label = getUserDisplayName(manager, "");
      if (!label) {
        continue;
      }
      byId.set(String(manager.id), label);
    }
  }
  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Whether a project matches the selected customer filter (empty = inactive).
 *
 * @param {Object} project - Project API row.
 * @param {Array<string>} selectedCustomerIds - Selected customer id strings.
 * @returns {boolean}
 */
export function projectMatchesCustomerFilter(project, selectedCustomerIds) {
  if (!selectedCustomerIds?.length) {
    return true;
  }
  const customerId = project?.customer?.id;
  if (customerId == null) {
    return false;
  }
  return selectedCustomerIds.includes(String(customerId));
}

/**
 * Whether a project matches the selected manager filter (OR: any selected manager).
 *
 * @param {Object} project - Project API row.
 * @param {Array<string>} selectedManagerIds - Selected manager id strings.
 * @returns {boolean}
 */
export function projectMatchesManagerFilter(project, selectedManagerIds) {
  if (!selectedManagerIds?.length) {
    return true;
  }
  const managerIds = (project?.managers || [])
    .map((manager) => (manager?.id == null ? null : String(manager.id)))
    .filter(Boolean);
  return selectedManagerIds.some((id) => managerIds.includes(id));
}

/**
 * Compare project numbers highest-first (numeric-aware).
 *
 * @param {Object} a - Project API row.
 * @param {Object} b - Project API row.
 * @returns {number}
 */
export function compareProjectNumbersDescending(a, b) {
  return String(b?.number || b?.project_number || "").localeCompare(
    String(a?.number || a?.project_number || ""),
    undefined,
    { numeric: true }
  );
}

/**
 * Compare project names A–Z (case-insensitive).
 *
 * @param {Object} a - Project API row.
 * @param {Object} b - Project API row.
 * @returns {number}
 */
export function compareProjectNamesAscending(a, b) {
  return getProjectTitle(a).localeCompare(getProjectTitle(b), undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

/**
 * Sum concrete + labor card metrics for activity sorting.
 *
 * @param {Object|null|undefined} opsSummary - Concrete/labor brief summary.
 * @returns {number}
 */
export function getProjectHomeOpsActivitySum(opsSummary) {
  return (
    (Number(opsSummary?.concreteToday) || 0) +
    (Number(opsSummary?.concrete7d) || 0) +
    (Number(opsSummary?.laborToday) || 0) +
    (Number(opsSummary?.labor7d) || 0)
  );
}

/**
 * Sum visible T&M + RFI chip counts for activity sorting.
 *
 * Closed RFIs are omitted from the card chips, so they are not included.
 *
 * @param {Object|null|undefined} tmSummary - T&M card summary.
 * @param {Object|null|undefined} rfiSummary - RFI card summary.
 * @returns {number}
 */
export function getProjectHomeTicketActivitySum(tmSummary, rfiSummary) {
  return (
    (Number(tmSummary?.toSend) || 0) +
    (Number(tmSummary?.toSign) || 0) +
    (Number(tmSummary?.toPrice) || 0) +
    (Number(tmSummary?.toSubmit) || 0) +
    (Number(rfiSummary?.toSend) || 0) +
    (Number(rfiSummary?.toClose) || 0)
  );
}

/**
 * Sort projects by recent concrete/labor then T&M/RFI activity.
 *
 * 1. Highest concrete+labor metric sum first.
 * 2. Among zero ops-sum projects, highest T&M+RFI chip sum first.
 * 3. Projects with neither last (project number descending as tiebreaker).
 *
 * @param {Array<Object>} projects - Filtered project rows.
 * @param {Map<string, Object>} opsSummariesByProjectId - Ops briefs by project id.
 * @param {Map<string, {tm?: Object, rfi?: Object}>} ticketSummariesByProjectId - Ticket summaries by project id.
 * @returns {Array<Object>}
 */
function sortProjectsByRecentActivity(
  projects,
  opsSummariesByProjectId,
  ticketSummariesByProjectId
) {
  const opsMap = opsSummariesByProjectId || new Map();
  const ticketMap = ticketSummariesByProjectId || new Map();
  return [...projects].sort((a, b) => {
    const aKey = String(a.id);
    const bKey = String(b.id);
    const aTickets = ticketMap.get(aKey);
    const bTickets = ticketMap.get(bKey);

    const aOpsSum = getProjectHomeOpsActivitySum(opsMap.get(aKey));
    const bOpsSum = getProjectHomeOpsActivitySum(opsMap.get(bKey));
    const aHasOps = aOpsSum > 0;
    const bHasOps = bOpsSum > 0;

    // Ops-activity projects always come before zero-ops projects.
    if (aHasOps !== bHasOps) {
      return aHasOps ? -1 : 1;
    }
    if (aHasOps && bHasOps && aOpsSum !== bOpsSum) {
      return bOpsSum - aOpsSum;
    }

    const aTicketSum = getProjectHomeTicketActivitySum(
      aTickets?.tm,
      aTickets?.rfi
    );
    const bTicketSum = getProjectHomeTicketActivitySum(
      bTickets?.tm,
      bTickets?.rfi
    );
    const aHasTickets = aTicketSum > 0;
    const bHasTickets = bTicketSum > 0;

    // Among zero-ops projects, ticket-activity projects come next.
    if (!aHasOps && !bHasOps && aHasTickets !== bHasTickets) {
      return aHasTickets ? -1 : 1;
    }
    if (
      !aHasOps &&
      !bHasOps &&
      aHasTickets &&
      bHasTickets &&
      aTicketSum !== bTicketSum
    ) {
      return bTicketSum - aTicketSum;
    }

    return compareProjectNumbersDescending(a, b);
  });
}

/**
 * Sort Projects Home rows by the selected sort mode.
 *
 * @param {Array<Object>} projects - Filtered project rows.
 * @param {Map<string, Object>} opsSummariesByProjectId - Ops briefs by project id.
 * @param {Map<string, {tm?: Object, rfi?: Object}>} ticketSummariesByProjectId - Ticket summaries by project id.
 * @param {Object} [options]
 * @param {string} [options.sortMode=PROJECTS_HOME_SORT_MODES.ACTIVITY] - Active sort mode.
 * @param {boolean} [options.reverseSort=false] - Reverse the selected sort order.
 * @returns {Array<Object>}
 */
export function sortProjectsForHomeCards(
  projects,
  opsSummariesByProjectId,
  ticketSummariesByProjectId,
  { sortMode = PROJECTS_HOME_SORT_MODES.ACTIVITY, reverseSort = false } = {}
) {
  let sorted;
  if (sortMode === PROJECTS_HOME_SORT_MODES.NUMBER) {
    sorted = [...projects].sort(compareProjectNumbersDescending);
  } else if (sortMode === PROJECTS_HOME_SORT_MODES.NAME) {
    sorted = [...projects].sort(compareProjectNamesAscending);
  } else {
    sorted = sortProjectsByRecentActivity(
      projects,
      opsSummariesByProjectId,
      ticketSummariesByProjectId
    );
  }

  if (reverseSort) {
    sorted.reverse();
  }
  return sorted;
}

/**
 * Card column count matching Tailwind ``md`` / ``xl`` breakpoints.
 *
 * @param {number} [width=typeof window !== "undefined" ? window.innerWidth : 0]
 * @returns {1|2|3}
 */
export function getProjectsHomeCardColumnCount(width) {
  const viewportWidth =
    typeof width === "number"
      ? width
      : typeof window !== "undefined"
        ? window.innerWidth
        : 0;
  if (viewportWidth >= 1280) {
    return 3;
  }
  if (viewportWidth >= 768) {
    return 2;
  }
  return 1;
}

/**
 * Distribute projects into columns in row-major order (left → right, top → bottom).
 *
 * @param {Array<Object>} projects - Sorted project rows.
 * @param {number} columnCount - Number of columns (1–3).
 * @returns {Array<Array<Object>>}
 */
export function distributeProjectsIntoColumns(projects, columnCount) {
  const count = Math.max(1, Number(columnCount) || 1);
  const columns = Array.from({ length: count }, () => []);
  for (let index = 0; index < projects.length; index += 1) {
    columns[index % count].push(projects[index]);
  }
  return columns;
}
